import { useEffect, useState } from 'react';
import { useAtom } from 'jotai';
import { userAPI } from '../UserAPI';
import { authenticationAtom } from '../State';

/**
 * Den Inhalt einer Ressource lesen — auch wenn nur eine Referenz ankommt.
 *
 * `formatResources` (tri-server) legt jede Ressource außer `source`/`image` als eigenes
 * Dokument ab und schickt in die Konversation NUR `{type, reference, _id, title}`. Der
 * volle Inhalt kommt erst über `getResource`. `FileResource` tut das seit jeher, die drei
 * Chip-Komponenten (toolChoices, actionChips, followUpSuggestions) taten es nicht: sie
 * lasen `resource.content || resource.data`, fanden im Stream nichts und rendern bei
 * weniger als zwei Einträgen `null` — die Werkzeugauswahl blieb also unsichtbar, bis ein
 * Reload die Nachricht aus der Datenbank holte, wo der Inhalt vollständig steht. Genau so
 * hat sich der Fehler gezeigt: „Reload shows them."
 *
 * Liegt der Inhalt inline vor, wird nichts nachgeladen — der Hook ist dann wirkungslos.
 */
export const useResolvedResourceContent = <T,>(resource: {
  data?: T;
  content?: T;
  reference?: string;
}): T | undefined => {
  const [authentication] = useAtom(authenticationAtom);
  const inline = resource?.content || resource?.data;
  const [loaded, setLoaded] = useState<T | undefined>(undefined);

  const reference = resource?.reference;
  const customer = authentication?.customer;

  useEffect(() => {
    if (inline || !reference || !customer) {
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const full: any = await userAPI.getResource(customer, reference);
        if (!cancelled) {
          setLoaded(full?.content || full?.data);
        }
      } catch (error) {
        // Eine nicht ladbare Referenz darf die Antwort nicht kaputtmachen — die Chips
        // sind eine Zugabe, keine Voraussetzung.
        console.warn('Resource reference could not be resolved', reference, error);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [inline, reference, customer]);

  return inline || loaded;
};
